import { useEffect, useRef, useState } from "react";

import {
  S3PresignedUploadScenario,
  UserInputAttachedFile,
} from "@intelligems/ig-types";
import { checkChatAttachment } from "@intelligems/ig-utils";
import { useGetS3PresignedUploadUrl } from "@src/api/organizations/get-s3-presigned-upload-url";
import { useUploadToPresignedUrl } from "@src/api/organizations/upload-to-presigned-url";
import { useProviderAttachments } from "@src/components/ai-elements/prompt-input";

/**
 * Bridge layer between `PromptInputProvider`'s attachment state and the
 * s3-files presigned upload flow. For each attachment the provider holds,
 * this hook tracks an upload lifecycle (`uploading → uploaded | error`),
 * runs the same `checkChatAttachment` validator the server uses
 * (so rejection reasons surface byte-for-byte in the composer chip), and
 * enforces a per-turn count cap.
 *
 * The hook does not modify the provider's array — it observes via context
 * and stores upload state in a parallel id-keyed map. Removing an
 * attachment from the provider naturally cleans up its upload entry.
 */

export type FileUploadStatus = "uploading" | "uploaded" | "error";

export interface FileUploadEntry {
  id: string;
  filename: string;
  mediaType: string;
  size: number;
  status: FileUploadStatus;
  /**
   * Durable `s3://{bucket}/{key}` reference set when status === "uploaded".
   * Never a presigned URL — the AI server resolves this to a fresh signed
   * GET URL at each read (LLM call + history emit) so nothing TTL'd is
   * embedded in persisted message state.
   */
  s3Url?: string;
  error?: string;
}

export interface UseFileUploadOptions {
  scenario: S3PresignedUploadScenario;
  /** UX-level cap on attachments per turn (e.g. Viv = 5). */
  maxPerTurn: number;
}

export type UseFileUploadResult = ReturnType<typeof useFileUpload>;

export const useFileUpload = ({
  scenario,
  maxPerTurn,
}: UseFileUploadOptions) => {
  const attachmentsCtx = useProviderAttachments();
  const presignMutation = useGetS3PresignedUploadUrl();
  const uploadMutation = useUploadToPresignedUrl();

  const [entries, setEntries] = useState<Record<string, FileUploadEntry>>({});

  // `processedIds` tracks the ids we've already kicked off processing for,
  // so React StrictMode's double-invocation of effects doesn't fire the
  // upload twice for the same attachment.
  const processedIds = useRef<Set<string>>(new Set());

  // `acceptedCount` is the source of truth for the maxPerTurn gate. We
  // mutate it synchronously inside `handleNewAttachment` (before any
  // setState/await) so rapid drops are gated atomically — relying on
  // React state for this would race because functional setState updates
  // run asynchronously with respect to the calling code.
  const acceptedCount = useRef(0);

  useEffect(() => {
    const currentIds = new Set(attachmentsCtx.files.map((f) => f.id));

    // Drop entries for attachments the user removed; release their cap slot
    // if they were holding one (non-error status).
    setEntries((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const [id, entry] of Object.entries(next)) {
        if (currentIds.has(id)) continue;
        delete next[id];
        changed = true;
        processedIds.current.delete(id);
        if (entry.status !== "error") {
          acceptedCount.current = Math.max(0, acceptedCount.current - 1);
        }
      }
      return changed ? next : prev;
    });

    // Process newly-added attachments. We dispatch through a ref so the
    // effect's only reactive dependency is `attachmentsCtx.files` — the
    // handler closes over many props/mutations (presignMutation,
    // uploadMutation, scenario, maxPerTurn) that we don't want to be
    // effect-restart triggers. The ref is updated below in render.
    for (const attachment of attachmentsCtx.files) {
      if (processedIds.current.has(attachment.id)) continue;
      processedIds.current.add(attachment.id);
      void handlerRef.current?.(attachment);
    }
  }, [attachmentsCtx.files]);

  // Refresh the dispatch ref on every render so it sees the latest
  // closure. Refs aren't reactive, so this won't retrigger the effect
  // above — exactly the "stable callback" property we need.
  const handlerRef = useRef<
    ((attachment: AttachmentLike) => Promise<void>) | null
  >(null);
  handlerRef.current = handleNewAttachment;

  async function handleNewAttachment(
    attachment: AttachmentLike
  ): Promise<void> {
    const filename = attachment.filename || "attachment";
    const mediaTypeFromCtx = attachment.mediaType || "";

    // Count cap — checked synchronously before any async work so a burst of
    // additions is gated correctly without races.
    if (acceptedCount.current >= maxPerTurn) {
      setEntries((prev) => ({
        ...prev,
        [attachment.id]: {
          id: attachment.id,
          filename,
          mediaType: mediaTypeFromCtx || "application/octet-stream",
          size: 0,
          status: "error",
          error: `Max ${maxPerTurn} attachments per message`,
        },
      }));
      return;
    }
    acceptedCount.current++;

    // Show "uploading" immediately so the tile animates through the whole
    // pipeline (fetch → compress → validate → presign → PUT), not just
    // during the S3 PUT at the end. Size/mediaType placeholders here get
    // overwritten once the blob is in hand. Error transitions below
    // replace this entry wholesale.
    setEntries((prev) => ({
      ...prev,
      [attachment.id]: {
        id: attachment.id,
        filename,
        mediaType: mediaTypeFromCtx || "application/octet-stream",
        size: 0,
        status: "uploading",
      },
    }));

    // Fetch the underlying file bytes from the provider's blob URL so we
    // have the real size + can PUT the bytes to S3. Failure here is
    // typically a revoked object URL — treat as a read error.
    let blob: Blob;
    try {
      const response = await fetch(attachment.url);
      blob = await response.blob();
    } catch {
      acceptedCount.current = Math.max(0, acceptedCount.current - 1);
      setEntries((prev) => ({
        ...prev,
        [attachment.id]: {
          id: attachment.id,
          filename,
          mediaType: mediaTypeFromCtx || "application/octet-stream",
          size: 0,
          status: "error",
          error: "Failed to read attached file",
        },
      }));
      return;
    }

    // Best-effort image compression. Skipped when Web Workers aren't
    // available because the lib's main-thread fallback would freeze the
    // UI for several seconds on phone-sized photos.
    blob = await maybeCompressImage(blob, filename);

    const mediaType =
      mediaTypeFromCtx || blob.type || "application/octet-stream";
    const size = blob.size;

    // Pre-upload validation runs the same function the server's
    // chat-attachment scenario handler runs, so the reason string the chip
    // surfaces matches what the server would reject with.
    const check = checkChatAttachment({ filename, mediaType, size });
    if (!check.accept) {
      acceptedCount.current = Math.max(0, acceptedCount.current - 1);
      setEntries((prev) => ({
        ...prev,
        [attachment.id]: {
          id: attachment.id,
          filename,
          mediaType,
          size,
          status: "error",
          error: check.reason,
        },
      }));
      return;
    }

    setEntries((prev) => ({
      ...prev,
      [attachment.id]: {
        id: attachment.id,
        filename,
        mediaType,
        size,
        status: "uploading",
      },
    }));

    try {
      const { presignedUrl, bucket, key } = await presignMutation.mutateAsync({
        scenario,
        filename,
        mediaType,
        size,
      });
      await uploadMutation.mutateAsync({
        presignedUrl,
        body: blob,
        contentType: mediaType,
      });
      // Store the durable `s3://{bucket}/{key}` reference, not a TTL'd presigned
      // URL. The AI server re-presigns at every read (LLM call, history emit),
      // so no expiring URL is embedded in persisted message state.
      setEntries((prev) => ({
        ...prev,
        [attachment.id]: {
          id: attachment.id,
          filename,
          mediaType,
          size,
          status: "uploaded",
          s3Url: `s3://${bucket}/${key}`,
        },
      }));
    } catch (err) {
      acceptedCount.current = Math.max(0, acceptedCount.current - 1);
      setEntries((prev) => ({
        ...prev,
        [attachment.id]: {
          id: attachment.id,
          filename,
          mediaType,
          size,
          status: "error",
          error: extractErrorMessage(err),
        },
      }));
    }
  }

  // Render entries in provider order (drag-drop / paste preserves order, so
  // chips appear next to each other in the order the user attached them).
  const orderedEntries = attachmentsCtx.files
    .map((f) => entries[f.id])
    .filter((e): e is FileUploadEntry => Boolean(e));

  const hasUploadsInProgress = orderedEntries.some(
    (e) => e.status === "uploading"
  );
  const hasErrors = orderedEntries.some((e) => e.status === "error");
  // Pair each successful upload with its provider blob URL so the optimistic
  // user message can render the local image immediately (the s3:// URL on
  // `.url` is not browser-fetchable). Server never reads `localPreviewUrl`.
  const successfulFiles: UserInputAttachedFile[] = orderedEntries
    .filter(
      (e): e is FileUploadEntry & { s3Url: string } =>
        e.status === "uploaded" && !!e.s3Url
    )
    .map((e) => {
      const providerFile = attachmentsCtx.files.find((f) => f.id === e.id);
      return {
        name: e.filename,
        size: e.size,
        mediaType: e.mediaType,
        url: e.s3Url,
        localPreviewUrl: providerFile?.url,
      };
    });

  const clearUploads = () => {
    attachmentsCtx.clear();
    setEntries({});
    processedIds.current.clear();
    acceptedCount.current = 0;
  };

  return {
    entries: orderedEntries,
    hasUploadsInProgress,
    hasErrors,
    successfulFiles,
    clearUploads,
    scenario,
    maxPerTurn,
  };
};

// Narrow shape we actually need from the provider's attachments. Avoid
// taking a hard dependency on Vercel AI SDK's exported `FileUIPart` here —
// we only care about what `PromptInputProvider` populates.
type AttachmentLike = {
  id: string;
  url: string;
  filename?: string;
  mediaType?: string;
};

/**
 * Resize + re-encode oversized images before they hit S3. Anthropic
 * downscales every image to 1568px on its longest edge internally, so
 * matching that cap is the maximum useful fidelity at the model — going
 * larger costs bandwidth without gaining detail.
 *
 * Returns the original blob unchanged when:
 *  - Web Workers aren't available (compression would block the UI thread)
 *  - The input isn't an image (PDFs, CSVs)
 *  - The input is already within the size/dimension caps (the lib's own
 *    fast path) or compression throws (HEIC decode failure, etc.)
 *
 * Quality is intentionally high (0.9) — these are visual references
 * driving design edits, so artifacts in fine detail would matter.
 */
async function maybeCompressImage(blob: Blob, filename: string): Promise<Blob> {
  if (typeof Worker === "undefined") return blob;
  if (!blob.type.startsWith("image/")) return blob;
  try {
    const { default: imageCompression } =
      await import("browser-image-compression");
    const file = new File([blob], filename, { type: blob.type });
    return await imageCompression(file, {
      maxSizeMB: 5,
      maxWidthOrHeight: 1568,
      useWebWorker: true,
      initialQuality: 0.9,
    });
  } catch {
    return blob;
  }
}

function extractErrorMessage(err: unknown): string {
  if (typeof err === "object" && err !== null) {
    // axios-style error: server's rejection reason is in response.data.
    const e = err as {
      response?: { data?: { message?: string } | string };
      message?: string;
    };
    const data = e.response?.data;
    if (typeof data === "string") return data;
    if (data && typeof data === "object" && typeof data.message === "string") {
      return data.message;
    }
    if (typeof e.message === "string") return e.message;
  }
  return "Upload failed";
}
